<template>
    <div id="test-app-dt">
        <div class="container">
            <DataTable
                :columns="productColumns"
                :items="items"
                :current-page="currentPage"
                :total-pages="totalPages"
                :enable-multi-select="false"
                :enable-drag-drop="false"
                @page-changed="handlePageChange"
                @sort="onSort"
            >
                <template #toolbar>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                        <div class="ppv2-flex-1"></div>

                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="onSearch" />
                        </div>
                    </div>
                </template>

                <template #column-status="{ item }">
                    <span :class="badgeClasses[item.status].class">
                        {{ badgeClasses[item.status].label }}
                    </span>
                </template>

                <template #column-consumption="{ item }">
                    <span>{{ item.consumption }} {{ $t('datatable_customer_vouchers_purchased_v2_edit.days') }}</span>
                    <span :class="statusTypeConso[item.type_conso].class">
                        {{ statusTypeConso[item.type_conso].label }}
                    </span>
                </template>

                <template #column-actions="{ item }">
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                        :style="{ fontSize: normalizeFontSize(18) }"
                    >
                        <a
                            v-if="item.item.has_last_invoice"
                            :href="`/invoice/${item.item.order_id}`"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_customer_products_purchased_v2_edit.actions.btn_receipt.title')"
                        >
                            <Receipt :size="18" />
                        </a>
                        <button
                            @click="handleCashRegister(item)"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_customer_products_purchased_v2_edit.actions.btn_box.title')"
                        >
                            <Banknote :size="18" />
                        </button>
                    </div>
                </template>
            </DataTable>
        </div>
    </div>
</template>

<script>
import DataTable from '../../Shared/DataTable.vue'
import { Receipt, Banknote } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../../V3/Experience/components/Search.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'App',
    props: ['user'],
    components: {
        DataTable,
        Receipt,
        Banknote,
        Search,
    },
    data() {
        return {
            // Data Table State
            items: [],
            loading: false,
            currentPage: 1,
            perPage: 10,
            totalEntries: 0,
            searchQuery: '',
            sortKey: '',
            sortOrder: 'asc',
            searchTimeout: null,

            statusTypeConso: [
                {
                    label: this.$t('datatable_customer_products_purchased_v2_edit.status_item.on_hold'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-green-600 ppv2-text-green-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                },
                {
                    label: this.$t('datatable_customer_products_purchased_v2_edit.status_item.finished'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-pink-300 ppv2-text-pink-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                },
            ],
            badgeClasses: [
                {
                    label: '-',
                    class: '-',
                },
                {
                    label: this.$t('datatable_customer_products_purchased_v2_edit.status_item.in_progess'),
                    class: 'btn-alt-info',
                },
                {
                    label: this.$t('datatable_customer_products_purchased_v2_edit.status_item.on_hold'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-amber-600 ppv2-text-amber-100 ppv2-text-xs ppv2-px-3 ppv2-py-1 ppv2-whitespace-nowrap',
                },
                {
                    label: this.$t('datatable_customer_products_purchased_v2_edit.status_item.paid'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-green-600 ppv2-text-green-100 ppv2-text-xs ppv2-px-3 ppv2-py-1 ppv2-whitespace-nowrap',
                },
                {
                    label: this.$t('datatable_customer_products_purchased_v2_edit.status_item.delivered'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-green-600 ppv2-text-green-100 ppv2-text-xs ppv2-px-3 ppv2-py-1 ppv2-whitespace-nowrap',
                },
                {
                    label: this.$t('datatable_customer_products_purchased_v2_edit.status_item.blocked'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-red-600 ppv2-text-red-100 ppv2-text-xs ppv2-px-3 ppv2-py-1 ppv2-whitespace-nowrap',
                },
                {
                    label: this.$t('datatable_customer_products_purchased_v2_edit.status_item.saved'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-amber-600 ppv2-text-amber-100 ppv2-text-xs ppv2-px-3 ppv2-py-1 ppv2-whitespace-nowrap',
                },
                {
                    label: this.$t('datatable_customer_products_purchased_v2_edit.status_item.fenced'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-amber-200 ppv2-text-amber-300d ppv2-text-xs ppv2-px-3 ppv2-py-1 ppv2-whitespace-nowrap',
                },
            ],
            // API Configuration
            apiHeaders: {
                Authorization: 'Bearer your-token-here',
                'X-Requested-With': 'XMLHttpRequest',
            },

            // Users Table Configuration
            productPurhaseApiUrl: '/back-office/v2/customer/' + this.user.id + '/ajaxProductPurchase',
            productColumns: [
                {
                    key: 'name',
                    label: this.$t('datatable_customer_products_purchased_v2_edit.name'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'type',
                    label: this.$t('datatable_customer_products_purchased_v2_edit.type'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'created_at',
                    label: this.$t('datatable_customer_products_purchased_v2_edit.created_at'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'price',
                    label: this.$t('datatable_customer_products_purchased_v2_edit.price'),
                    sortable: true,
                    type: 'number',
                },
                {
                    key: 'delivery',
                    label: this.$t('datatable_customer_products_purchased_v2_edit.delivery'),
                    sortable: false,
                    type: 'text',
                },
                {
                    key: 'status',
                    label: this.$t('datatable_customer_products_purchased_v2_edit.status'),
                    sortable: false,
                    type: 'badge',
                },
                {
                    key: 'staff',
                    label: this.$t('datatable_customer_products_purchased_v2_edit.staff'),
                    sortable: false,
                    type: 'text',
                },
                {
                    key: 'consumption',
                    label: this.$t('datatable_customer_products_purchased_v2_edit.consumption'),
                    sortable: false,
                    type: 'text',
                },
                {
                    key: 'actions',
                    label: this.$t('datatable_customer_products_purchased_v2_edit.action'),
                    sortable: false,
                    type: 'actions',
                },
            ],
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalEntries / this.perPage)
        },
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchData() {
            this.loading = true
            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.searchQuery,
                    sort_by: this.sortKey,
                    sort_order: this.sortOrder,
                }
                const response = await this.makeApiRequest(params)
                this.handleApiResponse(response)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.onError(error)
            } finally {
                this.loading = false
            }
        },
        async makeApiRequest(params) {
            const queryString = new URLSearchParams(params).toString()
            const url = `${this.productPurhaseApiUrl}?${queryString}`

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.apiHeaders,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            return await response.json()
        },
        handleApiResponse(response) {
            let _response = response.data
            if (_response.data && Array.isArray(_response.data)) {
                this.items = _response.data
                this.totalEntries = _response.total || _response.data.length
                this.onDataLoaded({ data: this.items, total: this.totalEntries })
            } else if (Array.isArray(_response)) {
                this.items = _response
                this.totalEntries = _response.length
                this.onDataLoaded({ data: this.items, total: this.totalEntries })
            } else {
                this.items = []
                this.totalEntries = 0
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchData()
            }, 500)
        },
        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },
        onDataLoaded(response) {
            console.log('Data loaded:', response)
        },
        onError(error) {
            console.error('Datatable error:', error)
            alert('Error loading data: ' + error.message)
        },
        handleCashRegister(item) {
            const form = document.createElement('form')
            form.method = 'POST'
            form.action = `${window.location.origin}/back-office/caisse/add`
            form.target = '_blank'

            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
            if (csrfToken) {
                const csrfInput = document.createElement('input')
                csrfInput.type = 'hidden'
                csrfInput.name = '_token'
                csrfInput.value = csrfToken
                form.appendChild(csrfInput)
            }

            const params = {
                priceModalPresta: item.item.price,
                priceModalPrestaHT: item.item.price_ht,
                prestationLabel: item.product.name,
                quantity: item.item.quantity ?? 1,
                timeQty: 30,
                prestaStatus: 1,
                customerSelect: item.item.user_id,
                item_id: item.product.id,
            }

            for (const key in params) {
                if (params.hasOwnProperty(key)) {
                    const input = document.createElement('input')
                    input.type = 'hidden'
                    input.name = key
                    input.value = params[key]
                    form.appendChild(input)
                }
            }

            document.body.appendChild(form)
            form.submit()
            document.body.removeChild(form)
        },
    },
}
</script>

<style scoped>
.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}
</style>
